import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty } from "@/components/PageHeader";
import { OvernightTag, SleepTag } from "@/components/ShiftChip";
import { ClockCard } from "@/pages/staff/ClockCard";
import { NotificationsCard } from "@/pages/staff/NotificationsCard";
import { api, errMsg } from "@/lib/api";
import { fmtDay, fmtTime, isOvernight } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";

export const ShiftCard = ({ s, action }) => (
  <div className={`rounded-xl border bg-white p-5 ${s.needs_ack && !s.acked_at ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200"}`} style={{ borderLeft: `5px solid ${s.shift_type?.colour || "#94a3b8"}` }} data-testid={`my-shift-${s.id}`}>
    {s.needs_ack && !s.acked_at && <p className="mb-2 text-xs font-bold uppercase tracking-wider text-amber-700">Updated or new shift</p>}
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{fmtDay(s.start)}</p>
        <p className="mt-1 font-mono text-lg font-semibold">{fmtTime(s.start)} – {fmtTime(s.end)}{isOvernight(s) && <OvernightTag />}</p>
        {isOvernight(s) && <p className="text-xs text-slate-500">Finishes {fmtDay(s.end)}</p>}
        <p className="mt-1 text-sm text-slate-600">{s.shift_type?.name || "Shift"}</p>
        {s.location_name && <p className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" />{s.location_name}</p>}
      </div>
      {s.shift_type?.is_sleepover && <SleepTag />}
    </div>
    {s.notes && <p className="mt-2 text-xs italic text-slate-500">{s.notes}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

const SwapDialog = ({ shift, onClose }) => {
  const [mates, setMates] = useState([]);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    api.get("/me/colleagues").then((r) => setMates(r.data));
  }, []);
  const send = async () => {
    try {
      await api.post("/me/requests", { type: "swap", shift_id: shift.id, target_staff_id: target, reason });
      toast.success("Swap request sent to your manager");
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="swap-dialog">
        <DialogHeader><DialogTitle>Request a swap</DialogTitle></DialogHeader>
        <p className="font-mono text-sm">{fmtDay(shift.start)} · {fmtTime(shift.start)}–{fmtTime(shift.end)}</p>
        <div className="space-y-1.5">
          <Label>Who will cover?</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="swap-target-select"><SelectValue placeholder="Choose colleague" /></SelectTrigger>
            <SelectContent>{mates.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="swap-reason-input" /></div>
        <Button className="rounded-full" disabled={!target} onClick={send} data-testid="swap-submit-btn">Send request</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function StaffHome() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [swap, setSwap] = useState(null);
  const load = useCallback(() => {
    api.get("/me/upcoming").then((r) => setShifts(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const ack = async (id) => {
    try {
      await api.post(`/me/shifts/${id}/ack`);
      toast.success("Thanks, your manager can see you've confirmed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <p className="text-sm text-slate-500">{user?.business?.name}</p>
        <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="page-title">Hi {user?.name?.split(" ")[0]}</h1>
      </div>
      <ClockCard />
      <NotificationsCard />
      <h2 className="font-display text-xl font-semibold">Upcoming shifts</h2>
      {shifts.length === 0 ? <Empty testId="my-shifts-empty">No upcoming shifts yet. You'll see them here once your manager publishes the rota.</Empty> : (
        <div className="space-y-3">
          {shifts.map((s) => (
            <ShiftCard key={s.id} s={s} action={
              <div className="flex flex-wrap items-center gap-2">
                {s.needs_ack && !s.acked_at && (
                  <Button size="sm" className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400" onClick={() => ack(s.id)} data-testid={`ack-shift-btn-${s.id}`}><CheckCircle2 className="mr-1 h-4 w-4" />Got it</Button>
                )}
                {s.needs_ack && s.acked_at && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`ack-done-${s.id}`}><CheckCircle2 className="h-4 w-4" />Confirmed</span>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSwap(s)} data-testid={`swap-btn-${s.id}`}><Repeat className="mr-1 h-4 w-4" />Request swap</Button>
              </div>
            } />
          ))}
        </div>
      )}
      {swap && <SwapDialog shift={swap} onClose={() => setSwap(null)} />}
    </div>
  );
}
